import axios from "./axios";

export interface SiteSettingsPayload {
  rowVersionToken: string;
  appName: string;
  headerLogoUrl: string;
  paramLogoUrl: string;
  faviconUrl: string;
  footerLogoUrl: string;
  footerDescription: string;
  defaultUserNameColor: string;
  supportEmail: string;
  businessEmail: string;
  googleSiteVerification: string;
  adsenseClientId: string;
  adsensePackagesSlot: string;
  adsenseUserPackagesSlot: string;
  discordUrl: string;
  instagramUrl: string;
  tiktokUrl: string;
  youtubeUrl: string;
  twitterUrl: string;
  facebookUrl: string;
  mailEventEmailVerification: boolean;
  mailEventPasswordReset: boolean;
  mailEventSecurityAlert: boolean;
  mailEventPaymentSuccess: boolean;
  mailEventGiftPackage: boolean;
  mailEventGiftZToken: boolean;
  mailEventSystemNotification: boolean;
}

export const SITE_SETTINGS_DEFAULTS: SiteSettingsPayload = {
  rowVersionToken: "",
  appName: "Zenitoon",
  headerLogoUrl: "",
  paramLogoUrl: "",
  faviconUrl: "",
  footerLogoUrl: "",
  footerDescription: "",
  defaultUserNameColor: "#FFFFFF",
  supportEmail: "business@zenitoon.com",
  businessEmail: "business@zenitoon.com",
  googleSiteVerification: "",
  adsenseClientId: "",
  adsensePackagesSlot: "",
  adsenseUserPackagesSlot: "",
  discordUrl: "",
  instagramUrl: "",
  tiktokUrl: "",
  youtubeUrl: "",
  twitterUrl: "",
  facebookUrl: "",
  mailEventEmailVerification: true,
  mailEventPasswordReset: true,
  mailEventSecurityAlert: true,
  mailEventPaymentSuccess: true,
  mailEventGiftPackage: true,
  mailEventGiftZToken: true,
  mailEventSystemNotification: true,
};

export async function getSiteSettings(): Promise<SiteSettingsPayload> {
  const response = await axios.get("/admin/site-settings");
  return normalizeSiteSettings(response.data);
}

export async function saveSiteSettings(
  payload: SiteSettingsPayload,
): Promise<{ success: boolean; message?: string; rowVersionToken?: string }> {
  const response = await axios.post("/admin/site-settings", payload);
  return {
    success: Boolean(response.data?.success ?? true),
    message: typeof response.data?.message === "string" ? response.data.message : undefined,
    rowVersionToken: typeof response.data?.rowVersionToken === "string" ? response.data.rowVersionToken : undefined,
  };
}

function normalizeSiteSettings(payload: unknown): SiteSettingsPayload {
  const source = typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
  return {
    rowVersionToken: normalizeOptional(source.rowVersionToken),
    appName: normalizeRequired(source.appName, SITE_SETTINGS_DEFAULTS.appName),
    headerLogoUrl: normalizeOptional(source.headerLogoUrl),
    paramLogoUrl: normalizeOptional(source.paramLogoUrl),
    faviconUrl: normalizeOptional(source.faviconUrl),
    footerLogoUrl: normalizeOptional(source.footerLogoUrl),
    footerDescription: normalizeOptional(source.footerDescription),
    defaultUserNameColor: normalizeRequired(source.defaultUserNameColor, SITE_SETTINGS_DEFAULTS.defaultUserNameColor),
    supportEmail: normalizeRequired(source.supportEmail, SITE_SETTINGS_DEFAULTS.supportEmail),
    businessEmail: normalizeRequired(source.businessEmail, SITE_SETTINGS_DEFAULTS.businessEmail),
    googleSiteVerification: normalizeOptional(source.googleSiteVerification),
    adsenseClientId: normalizeOptional(source.adsenseClientId),
    adsensePackagesSlot: normalizeOptional(source.adsensePackagesSlot),
    adsenseUserPackagesSlot: normalizeOptional(source.adsenseUserPackagesSlot),
    discordUrl: normalizeOptional(source.discordUrl),
    instagramUrl: normalizeOptional(source.instagramUrl),
    tiktokUrl: normalizeOptional(source.tiktokUrl),
    youtubeUrl: normalizeOptional(source.youtubeUrl),
    twitterUrl: normalizeOptional(source.twitterUrl),
    facebookUrl: normalizeOptional(source.facebookUrl),
    mailEventEmailVerification: normalizeBoolean(source.mailEventEmailVerification, SITE_SETTINGS_DEFAULTS.mailEventEmailVerification),
    mailEventPasswordReset: normalizeBoolean(source.mailEventPasswordReset, SITE_SETTINGS_DEFAULTS.mailEventPasswordReset),
    mailEventSecurityAlert: normalizeBoolean(source.mailEventSecurityAlert, SITE_SETTINGS_DEFAULTS.mailEventSecurityAlert),
    mailEventPaymentSuccess: normalizeBoolean(source.mailEventPaymentSuccess, SITE_SETTINGS_DEFAULTS.mailEventPaymentSuccess),
    mailEventGiftPackage: normalizeBoolean(source.mailEventGiftPackage, SITE_SETTINGS_DEFAULTS.mailEventGiftPackage),
    mailEventGiftZToken: normalizeBoolean(source.mailEventGiftZToken, SITE_SETTINGS_DEFAULTS.mailEventGiftZToken),
    mailEventSystemNotification: normalizeBoolean(source.mailEventSystemNotification, SITE_SETTINGS_DEFAULTS.mailEventSystemNotification),
  };
}

function normalizeOptional(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

function normalizeRequired(value: unknown, fallback: string): string {
  const normalized = normalizeOptional(value);
  return normalized.length > 0 ? normalized : fallback;
}

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return fallback;
}
